// services/api.ts
import axios from 'axios';

const API_URL = "https://aksandesteknodebackend.aksan.cloud/api";

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});

// Request interceptor - token ekle
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - rate limit & auth
api.interceptors.response.use(
  (response) => {
    console.log(`✅ API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('❌ API Response Error:', {
      url: error.config?.url,
      status: error.response?.status,
      message: error.message,
    });

    if (error.response?.status === 429) {
      console.warn('⚠️ Rate limit exceeded, please wait...');
    }

    if (error.response?.status === 401) {
      console.log('🔐 Authentication failed, redirecting to login...');
      localStorage.removeItem('token');
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

// ---- API WRAPPERS ----
export const authAPI = {
  login: (email: string, password: string) => api.post('/auth/login', { email, password }),
  verify: () => api.get('/auth/verify'),
};
export const reportsAPI = {
  getMonthlyReports: (year?: number) => 
    api.get(`/reports/monthly?year=${year || new Date().getFullYear()}`),
  
  getStatusDistribution: () => 
    api.get('/reports/status-distribution'),
  
  getModuleDistribution: () => 
    api.get('/reports/module-distribution'),

  getCompanyDistribution: () => 
    api.get('/reports/company-distribution'),
  
  getPriorityDistribution: () => 
    api.get('/reports/priority-distribution'),
  
  getGeneralStats: () => 
    api.get('/reports/general-stats'),
}

export const ticketsAPI = {
  // ✅ BASİT VE ETKİLİ: Direkt params gönder
  getTickets: (params?: any) => {
    console.log('📤 API Call - getTickets with params:', params);
    return api.get('/tickets', { params });
  },
  
  getUnreadMessagesCount: (ticketId: number) => 
    api.get(`/tickets/${ticketId}/unread-messages/count`),
  
  markMessagesAsRead: (ticketId: number) => 
    api.put(`/tickets/${ticketId}/messages/mark-as-read`),
  
  getAllUnreadMessagesCounts: () => 
    api.get('/tickets/unread-messages/counts'),

  getTicket: (id: number) => api.get(`/tickets/${id}`),
  createTicket: (data: any) => api.post('/tickets', data),
  updateTicket: (id: number, data: any) => api.put(`/tickets/${id}`, data),
  deleteTicket: (id: number) => api.delete(`/tickets/${id}`),
  assignTicket: (id: number, data: any) => api.put(`/tickets/${id}/assign`, data),
  
  editTicket: (id: number, data: any) => {
    const payload: any = {};
    const keys = ['module_id', 'subject', 'description', 'email', 'priority_id', 'mail_content'];

    for (const k of keys) {
      const v = data[k];
      if (v !== undefined && v !== null && v !== '') {
        if (k === 'module_id' || k === 'priority_id') {
          const n = Number(v);
          if (!Number.isNaN(n)) payload[k] = n;
        } else {
          payload[k] = v;
        }
      }
    }

    return api.put(`/tickets/${id}/edit`, payload).catch((err) => {
      if (err?.response?.status === 404) {
        return api.put(`/tickets/${id}`, payload);
      }
      throw err;
    });
  },
  // Dosya işlemleri
  uploadFiles: (ticketId: number, formData: FormData) =>
    api.post(`/tickets/${ticketId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  getFiles: (ticketId: number) => api.get(`/tickets/${ticketId}/files`),

  downloadFile: (fileId: number) =>
    api.get(`/tickets/files/download?fileId=${fileId}`, { 
      responseType: 'blob'
    }),

  deleteFile: (fileId: number) => api.delete(`/tickets/files/${fileId}`),
  // MESAJLAŞMA ENDPOINT'LERİ
  getMessages: (ticketId: number) => api.get(`/tickets/${ticketId}/messages`),
  sendMessage: (ticketId: number, data: any) => api.post(`/tickets/${ticketId}/messages`, data),
  sendMessageWithFile: (ticketId: number, formData: FormData) => 
    api.post(`/tickets/${ticketId}/messages/with-file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  downloadMessageFile: (messageId: number) =>
    api.get(`/tickets/messages/files/download?messageId=${messageId}`, { 
      responseType: 'blob'
    }),
};

export const companiesAPI = {
  getCompanies: () => api.get('/companies'),
  getCompany: (id: number) => api.get(`/companies/${id}`),
  createCompany: (data: any) => api.post('/companies', data),
  updateCompany: (id: number, data: any) => api.put(`/companies/${id}`, data),
  deleteCompany: (id: number) => api.delete(`/companies/${id}`),
};

export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

export const usersAPI = {
  getSupportUsers: () => api.get('/users/support'),
  getCompanyUsers: (companyId: number) => api.get(`/users/company/${companyId}`),
  createUser: (data: any) => api.post('/users', data),
  updateUser: (id: number, data: any) => api.put(`/users/${id}`, data),
  deleteUser: (id: number) => api.delete(`/users/${id}`),
};

export const systemAPI = {
  getModules: () => api.get('/modules'),
  getStatuses: () => api.get('/statuses'),
  getPriorities: () => api.get('/priorities'),
};

export const healthCheck = () => api.get('/health');

export default api;